import { Routes } from '@common/constants';
import { ChevronRightIcon } from '@common/icons/ChevronRightIcon';
import { useTranslations } from '@lang';
import React, { useState } from 'react';
import { Link } from 'shared-routing';
import { Price } from '../Price';
import { Stack } from '../Stack';
import {
  BasketDrawerAbsolutePositioning,
  BasketDrawerContainer,
  BasketDrawerFixed,
  BasketDrawerPosition,
  BasketDrawerTitle,
  BasketOverview,
  BasketTextAndView,
  ButtonGroup,
  CloseContainer,
  ContentContainer,
  ContinueBrowsingButton,
  ContinueBrowsingIcon,
  ContinueBrowsingLink,
  Divider,
  GlobalStyle,
  HeaderContent,
  ItemText,
  ItineraryToggle,
  Listing,
  Overlay,
  Panel,
  PanelBackground,
  StickyContainer,
  StyledButton,
  StyledClose,
  StyledPrice,
  TitleWrapper,
  TotalText,
} from './BasketDrawer.styles';
import { Item } from 'shared-types';
import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import { BasketDrawerListing } from './BasketDrawerListing';
import { BasketDrawerItinerary } from './BasketDrawerItinerary/BasketDrawerItinerary';
import { hasOnlyNoValueTableBookings } from '@utils/basket/noValueTableBooking';
import { getItineraryItems } from '@utils/getItineraryView';
import { useLabels } from '@features/Menu/useLabels';

type BasketDrawerProps = {
  title?: string;
  onClose: () => void;
  open: boolean;
  totalCost: number;
  numberOfItems: number;
  currencyCode: string;
  items: Item[] | FulfilableBasketItem[];
  modules: string[];
  hideArrivalTimes: boolean;
  showContinueShopping?: boolean;
  onCheckout?: () => void;
  isCheckoutLoading?: boolean;
};

export function BasketDrawer({
  open,
  onClose,
  title,
  totalCost,
  numberOfItems,
  currencyCode,
  items,
  modules,
  hideArrivalTimes,
  showContinueShopping = false,
  onCheckout,
  isCheckoutLoading = false,
}: BasketDrawerProps) {
  const { t } = useTranslations();
  const labels = useLabels();
  const [showItineraryView, setShowItineraryView] = useState(false);
  const itineraryItems = getItineraryItems(items, t, labels);
  const hasItineraryItems = itineraryItems.some((item) => item.hasItems);
  const showTotal = !hasOnlyNoValueTableBookings(items);

  return (
    <BasketDrawerContainer
      data-cy-id="basket-drawer"
      role="dialog"
      show={open}
      aria-labelledby="basket-drawer-title"
      aria-modal="true"
      data-testid="basket-drawer-container"
    >
      {open && <Overlay onClick={onClose} />}
      <GlobalStyle />
      <BasketDrawerFixed>
        <BasketDrawerAbsolutePositioning>
          <BasketDrawerPosition>
            <Panel>
              <PanelBackground>
                <ContentContainer>
                  <HeaderContent>
                    <CloseContainer>
                      <StyledClose
                        data-testid="close-basket-drawer"
                        onClick={onClose}
                        aria-hidden="true"
                      />
                    </CloseContainer>
                  </HeaderContent>
                  <TitleWrapper>
                    <BasketDrawerTitle>{title}</BasketDrawerTitle>
                    {hasItineraryItems && (
                      <ItineraryToggle onClick={() => setShowItineraryView((val) => !val)}>
                        {showItineraryView ? t('basket.itemisedView') : t('basket.itineraryView')}
                      </ItineraryToggle>
                    )}
                  </TitleWrapper>
                  <Listing>
                    {showItineraryView ? (
                      <BasketDrawerItinerary items={items} isBasketDrawer />
                    ) : (
                      <BasketDrawerListing
                        items={items}
                        modules={modules}
                        hideArrivalTimes={hideArrivalTimes}
                      />
                    )}
                  </Listing>
                </ContentContainer>
                <StickyContainer>
                  <Divider />
                  {showTotal && (
                    <BasketOverview>
                      <BasketTextAndView>
                        <Stack direction="vertical" gap={4}>
                          <div>
                            <TotalText>{t('basketDrawer.total')}</TotalText>
                            <ItemText marginLeft="22px">
                              {numberOfItems > 1
                                ? `${numberOfItems} ${t('basketDrawer.numberOfItems.plural')}`
                                : `${numberOfItems} ${t('basketDrawer.numberOfItems.singular')}`}
                            </ItemText>
                          </div>
                        </Stack>
                        <StyledPrice>
                          <Price
                            price={{
                              amount: totalCost,
                              currencyCode,
                            }}
                          />
                        </StyledPrice>
                      </BasketTextAndView>
                    </BasketOverview>
                  )}
                  <ButtonGroup>
                    <Link route={Routes.BASKET}>
                      <StyledButton>{t('basketDrawer.viewBasket')}</StyledButton>
                    </Link>
                    <StyledButton
                      isPrimary
                      onClick={onCheckout}
                      disabled={isCheckoutLoading || !onCheckout}
                    >
                      {t('basketDrawer.checkout')}
                    </StyledButton>
                    {showContinueShopping && (
                      <ContinueBrowsingLink route={Routes.HOME}>
                        <ContinueBrowsingButton type="button" onClick={onClose}>
                          {t('basket.continueShopping')}
                          <ContinueBrowsingIcon>
                            <ChevronRightIcon />
                          </ContinueBrowsingIcon>
                        </ContinueBrowsingButton>
                      </ContinueBrowsingLink>
                    )}
                  </ButtonGroup>
                </StickyContainer>
              </PanelBackground>
            </Panel>
          </BasketDrawerPosition>
        </BasketDrawerAbsolutePositioning>
      </BasketDrawerFixed>
    </BasketDrawerContainer>
  );
}
